import { MouseEventHandler, useEffect } from 'react';
import { IAppointment, useSendPickupReceipt } from '../../../../../API/GoMoto';
import { useLocalization } from '../../../../../context/Localization/localization';
import { useSetHeaderProps, useSetKioskBannerVisibility } from '../../../../../context/Page/page';
import { CTAButton } from '../../../../../controls/ButtonBase';
import { Heading } from '../../../../../controls/Heading/Heading';
import { ScrollableBody } from '../../../../Layout/ScrollableBody';
import { GMButtonGroup } from '../../../../../controls/ButtonGroup';
import { GMRoundedIcon, GMSizedIcon } from '../../../../../controls/Icon';
import { ContactPreferenceEnum } from '../../../../../context/Appointment/appointment';
import { useKioskState } from '../../../../../context/Kiosk/kiosk';
import { formatAsPhoneNumber } from '../../../../../helpers/Number';
import { GoMotoComponent, GoMotoFunction } from '../../../../../API/GoMoto.types';
import { PickUpCompleteProps } from './PickUpComplete.types';
import { LocalizationType } from '../../../../../context/Localization/localization.types';

/**
 * The final screen that the customer sees after retrieving their key or performing a key
 * exchange. This screen displays a thank you  message and provides the user with the ability
 * to request a receipt of their pick-up emailed to them. When they are finished, they can click the
 * button to finish the pick-up.
 *
 * @param onFinish - {@link PickUpCompleteProps.onFinish onFinish} description.
 * @param appointment - {@link PickUpCompleteProps.appointment appointment} description.
 */
export const PickUpComplete: GoMotoComponent<PickUpCompleteProps> = ({
    onFinish,
    appointment,
}) => {
    const Localization: LocalizationType = useLocalization();
    const {
        dealer: {
            customerTextingActive,
        },
    } = useKioskState();
    const resend = useSendPickupReceipt();
    const showBanner = useSetKioskBannerVisibility();
    const setHeader = useSetHeaderProps();
    const showResendButton = appointment.paymentStatus !== 'no payment due';

    const resendReceipt: MouseEventHandler<HTMLElement> = async () => {
        if (appointment.key) {
            const contactPreference: IAppointment['pickupContactPreference'] = appointment.pickupContactPreference ?
                appointment.pickupContactPreference :
                appointment.contactPreference;

            await resend({
                appointmentKey: appointment.key,
                sendText: contactPreference === ContactPreferenceEnum.Text || contactPreference === ContactPreferenceEnum.Both,
                sendEmail: (
                    contactPreference === ContactPreferenceEnum.Email
                    || contactPreference === ContactPreferenceEnum.Both
                    || contactPreference === ContactPreferenceEnum.Phone
                    || !contactPreference
                )
                    && ((appointment.pickupEmail || appointment.email) ?? '').trim() !== '',
                shouldUsePickupEmail: true,
                shouldUsePickupPhone: true,
            });
        }
    };

    useEffect(() => {
        showBanner(true);
        setHeader(undefined);
    }, [setHeader, showBanner]);

    const renderThankYouMessage: GoMotoFunction<[void], string | (string | JSX.Element)[]> = () => {
        const contactPreference: IAppointment['pickupContactPreference'] = appointment.pickupContactPreference || appointment.contactPreference;
        const effectiveEmail: string = appointment.pickupEmail || appointment.email;
        const effectivePhone: string = appointment.pickupPhone || appointment.phone;

        if (customerTextingActive) {
            if (contactPreference === ContactPreferenceEnum.Both) {
                return Localization.formatString(
                    Localization.ThankYouForServicingYourVehicleAReceiptHasBeenSentToMultiple,
                    <a href={`mailto:${effectiveEmail}`}>{effectiveEmail}</a>,
                    <a href={`tel:${effectivePhone}`}>{formatAsPhoneNumber(effectivePhone ?? '')}</a>,
                );
            }
            else if (contactPreference === ContactPreferenceEnum.Text) {
                return Localization.formatString(
                    Localization.ThankYouForServicingYourVehicleAReceiptHasBeenSent,
                    <a href={`tel:${effectivePhone}`}>{formatAsPhoneNumber(effectivePhone ?? '')}</a>,
                );
            }
        }

        return Localization.formatString(
            Localization.ThankYouForServicingYourVehicleAReceiptHasBeenSent,
            <a href={`mailto:${effectiveEmail}`}>{effectiveEmail}</a>,
        );
    };

    return (
        <ScrollableBody
            heading={
                <Heading
                    primaryHeading={Localization.PickupComplete}
                    secondaryHeading={
                        <>
                            <p>
                                {renderThankYouMessage()}
                            </p>
                            <p style={{ fontSize: '24px' }}>{Localization.HaveAGreatDay}</p>
                        </>
                    }
                    roundedIcon={
                        <GMRoundedIcon
                            roundedBackgroundSize={'large'}
                            themeType={'secondary'}
                        >
                            <GMSizedIcon iconType={'Vehicle'} iconSize={'extraLarge'} />
                        </GMRoundedIcon>
                    }
                />
            }
            proceedButtons={
                <GMButtonGroup
                    alignment={'center'}
                    buttons={[
                        (
                            <CTAButton
                                styleType={'primary'}
                                title={Localization.ClickToConfirmCheckInComplete}
                                label={Localization.Finish}
                                onClick={onFinish}
                                kioskOptions={{ square: true }}
                                id={Localization.ClickToConfirmCheckInComplete}
                                ariaLabel={Localization.ClickToConfirmCheckInComplete}
                            />
                        ),
                        (showResendButton ? (
                            <CTAButton
                                styleType={'secondary'}
                                onClick={resendReceipt}
                                title={Localization.ClickToRequestReceipt}
                                label={Localization.ResendReceipt}
                                kioskOptions={{
                                    squareOptions: {
                                        roundedIcon: (
                                            <GMRoundedIcon
                                                themeType={'primary'}
                                                allowInvertedPrimary
                                                roundedBackgroundSize={'large'}
                                            >
                                                <GMSizedIcon iconSize={'custom'} iconType={'Send'} />
                                            </GMRoundedIcon>
                                        ),
                                    },
                                    square: true,
                                }}
                                id={Localization.ClickToRequestReceipt}
                                ariaLabel={Localization.ClickToRequestReceipt}
                            />
                        ) : null),
                    ]}
                />
            }
            unsetButtons
        />
    );
};
